/**
 * Whether to send the browser straight to Entra instead of showing a page
 * with a sign-in button on it.
 *
 * The console has exactly one way in, so a landing page asks the user to
 * confirm something they have no alternative to. Redirecting immediately is
 * what single sign-on is supposed to feel like.
 *
 * The hazard is a redirect loop, and it is not hypothetical. Entra can send
 * a browser back here still unauthenticated for reasons the SPA cannot
 * distinguish from a fresh visit -- the user is not assigned to the
 * application, a Conditional Access policy refuses the device, they cancel
 * at the account picker, or the token cache cannot be written. Redirect on
 * arrival with no memory of having just done so and the browser bounces
 * between two origins until someone closes the tab, with no page ever
 * rendering long enough to say why.
 *
 * So the attempt is claimed once per tab. A second arrival in the same
 * session renders the sign-in page instead, which is then a genuine
 * fallback rather than the default path.
 *
 * `sessionStorage`, to match the lifetime of MSAL's token cache: a new tab
 * is a new session and should redirect again, and closing the browser must
 * not leave a marker that suppresses it.
 */

const MARKER = 'csa.auto-signin-attempted';

/** The subset used here, so tests can supply their own. */
export type SessionLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Accepts a candidate store only if it can actually be written to.
 *
 * Some privacy modes expose `sessionStorage` and then throw on use, so
 * presence is not enough -- hence the probe write. Returning null means auto
 * sign-in is off rather than unprotected: with nowhere to record the attempt
 * there is nothing to stop a loop, and MSAL could not cache a token in such
 * a browser anyway.
 */
export function usableStore(candidate: SessionLike | null | undefined): SessionLike | null {
  if (!candidate) return null;
  try {
    const probe = `${MARKER}.probe`;
    candidate.setItem(probe, '1');
    candidate.removeItem(probe);
    return candidate;
  } catch {
    return null;
  }
}

/** The real one. Reading the property can itself throw, so it is guarded too. */
export function sessionStore(): SessionLike | null {
  try {
    return usableStore(window.sessionStorage);
  } catch {
    return null;
  }
}

/**
 * True when an automatic redirect has not yet been attempted in this
 * session. Reads without claiming, so the first paint can show the redirect
 * notice rather than flashing the sign-in card for a frame.
 */
export function autoSignInPending(store: SessionLike | null = sessionStore()): boolean {
  return store !== null && store.getItem(MARKER) === null;
}

/**
 * Claims the one automatic attempt this session gets, returning whether the
 * caller may proceed. Claim and test are one operation on purpose: React
 * invokes effects twice under StrictMode, and two `loginRedirect` calls race
 * each other through the same PKCE exchange.
 */
export function claimAutoSignIn(store: SessionLike | null = sessionStore()): boolean {
  if (store === null || store.getItem(MARKER) !== null) return false;
  store.setItem(MARKER, '1');
  return true;
}

/**
 * Forgets the attempt, called once a sign-in has actually succeeded.
 *
 * Without this the marker would outlive the thing it guards: a token that
 * expires an hour later would drop the user on the sign-in page in a tab
 * that has already proved it can complete a redirect.
 */
export function clearAutoSignIn(store: SessionLike | null = sessionStore()): void {
  store?.removeItem(MARKER);
}
